/**
 * «¿Dónde ha ido el dinero este año?» (puro): de lo que tenías disponible el 1 de enero a lo de hoy, por destino.
 * Suma los motivos de cada mes (liquidityBreakdown) movimiento a movimiento: lo que entró un mes y contó en otro, dentro
 * del año, sale dos veces con signo contrario y se anula; solo quedan los que cruzan el cambio de año.
 * Después agrupa por destino: tus otras cuentas (sigue siendo tuyo), lo pagado por otras personas, lo pagado desde
 * cuentas apartadas, cobros y pagos de otro año y ajustes. start + savings + Σ pasos = now.
 */
import { round2 } from '@pf/domain';
import type { Flow } from '@pf/workbook';
import type { LiquidityYear } from './liquidity-model.js';
import type { Breakdown, BreakdownItem, BreakdownKey } from './liquidity-breakdown.js';
import { SETTLE_CATEGORY } from './tricount-settle.js';

export type StepKey = 'own' | 'others' | 'reservedPay' | 'calendar' | 'adjust' | 'small';
export interface StepLine { label: string; total: number; items: BreakdownItem[] }
export interface BridgeStep { key: StepKey; label: string; short: string; note: string; total: number; lines: StepLine[] }
export interface YearBridge {
  start: number; now: number; savings: number; steps: BridgeStep[];
  /** Lo que cambió tu patrimonio en el año (todas las capas), si se conoce: lo pone quien lo sabe. */
  wealthChange: number | null;
}

/** Los motivos de menos de esto van juntos en «Pequeños ajustes». */
export const SMALL_STEP = 20;

const STEP: Record<Exclude<StepKey, 'small'>, { label: [string, string]; short: string; note: string }> = {
  own: { label: ['A tus otras cuentas', 'Desde tus otras cuentas'], short: 'Otras cuentas',
    note: 'Lo que pasas a fondos o a otras cuentas: ya no está en tu dinero disponible, pero si la cuenta es tuya sigue siendo tuyo (está en tu patrimonio).' },
  others: { label: ['Pagado por otras personas', 'Lo que te han pagado otras personas'], short: 'Por otros',
    note: 'La parte de otras personas en lo que pagaste tú (gastos compartidos, cuenta conjunta), menos lo que te han devuelto.' },
  reservedPay: { label: ['Pagado desde cuentas apartadas', 'Pagado desde cuentas apartadas'], short: 'Apartadas',
    note: 'Gastos tuyos que no salieron de tu dinero disponible: salieron cuando lo apartaste.' },
  calendar: { label: ['Cobros y pagos de otro año', 'Cobros y pagos de otro año'], short: 'Otro año',
    note: 'Movimientos que cruzan el cambio de año (p. ej. la nómina de enero, que entró en diciembre). Los del resto del año se compensan entre sí y no salen.' },
  adjust: { label: ['Ajustes y otros', 'Ajustes y otros'], short: 'Ajustes',
    note: 'Ajustes, saldos apuntados a mano y cuentas que no son dinero disponible.' },
};

/** Dónde va cada movimiento: el paso y la línea dentro de él. */
function place(key: BreakdownKey, x: BreakdownItem, flows: Flow[]): [Exclude<StepKey, 'small'>, string] {
  switch (key) {
    case 'reserved': return ['own', `acc:${x.dest}`];
    // Un traspaso con sus dos lados que mueve dinero es la parte de la otra persona en una cuenta compartida
    case 'transfer': return x.account.includes('→') ? ['others', `share:${x.account}`] : ['own', `acc:${x.dest}`];
    case 'excluded':
      if (x.category === SETTLE_CATEGORY) return ['others', 'tricount'];
      return flows.some(f => f.date.slice(0, 7) === x.date.slice(0, 7) && Math.abs(f.amount + x.amount) < 1) ? ['own', 'funds'] : ['adjust', 'adjust'];
    case 'shared': return ['others', x.effect < 0 ? 'shared-out' : 'shared-back'];
    case 'reservedPay': return ['reservedPay', `acc:${x.account}`];
    case 'shiftIn': case 'shiftOut': return ['calendar', `cat:${x.category || x.concept}`];
    case 'outside': return ['adjust', 'outside'];
    default: return ['adjust', 'other'];
  }
}

function lineLabel(step: StepKey, key: string, total: number): string {
  const [kind = '', ...rest] = key.split(':'), name = rest.join(':');
  if (kind === 'acc') return step === 'reservedPay' ? `Desde ${name}` : name ? `${total < 0 ? 'A' : 'Desde'} ${name}` : `${total < 0 ? 'A' : 'Desde'} cuentas que no están en tu libro`;
  if (kind === 'share') return `${name}: la parte de la otra persona`;
  if (kind === 'cat') return name;
  return ({
    funds: total < 0 ? 'A fondos' : 'Sacado de fondos',
    tricount: total > 0 ? 'Lo que te pagaron al saldar Tricount' : 'Lo que pagaste al saldar Tricount',
    'shared-out': 'Su parte de gastos que pagaste enteros',
    'shared-back': 'Lo que te devolvieron de gastos compartidos',
    adjust: 'Ajustes y saldos', outside: 'Cuentas que no son dinero disponible', other: 'Otros ajustes',
    patrimonio: 'Saldos apuntados a mano en Patrimonio', cents: 'Redondeos',
  } as Record<string, string>)[kind] ?? key;
}

export function yearBridge(l: LiquidityYear, breakdown: (month: string) => Breakdown | null, flows: Flow[] = []): YearBridge | null {
  if (l.start === null || l.now === null) return null;
  // Movimiento a movimiento: lo que sale en dos meses (entró en uno, cuenta en otro) se suma y, dentro del año, da 0
  const byId = new Map<string, { key: BreakdownKey; item: BreakdownItem; weight: number }>();
  let savings = 0, residual = 0;
  for (const p of l.points) {
    if (p.change === null) continue;
    savings += p.savings;
    // También los meses en que todo cuadra: ahí puede estar un lado de una nómina que se compensa con otro mes
    const b = p.other !== null ? breakdown(p.month) : null;
    // Sin desglose, lo no explicado es «otros» menos lo de fondos (que ya está en los movimientos)
    if (!b) { residual += (p.other ?? 0) - p.invested; continue; }
    residual += b.residual;
    for (const g of b.all) for (const x of g.items) {
      const had = byId.get(x.id);
      // Se queda la frase del lado que más pesa (p. ej. «es de enero, pero entró en diciembre»)
      if (!had) byId.set(x.id, { key: g.key, item: { ...x }, weight: Math.abs(x.effect) });
      else {
        const effect = round2(had.item.effect + x.effect);
        if (Math.abs(x.effect) > had.weight) byId.set(x.id, { key: g.key, item: { ...x, effect }, weight: Math.abs(x.effect) });
        else had.item.effect = effect;
      }
    }
  }
  const lines = new Map<Exclude<StepKey, 'small'>, Map<string, BreakdownItem[]>>();
  const add = (step: Exclude<StepKey, 'small'>, line: string, item?: BreakdownItem, total?: number) => {
    const m = lines.get(step) ?? lines.set(step, new Map()).get(step)!;
    const list = m.get(line) ?? m.set(line, []).get(line)!;
    if (item) list.push(item);
    else if (total) list.push({ id: '', date: '', concept: '', account: '', amount: 0, effect: total, why: '', category: '', moved: '', counts: '', dest: '' });
  };
  for (const { key, item } of byId.values()) if (Math.abs(item.effect) >= 0.5) add(...place(key, item, flows), item);
  if (Math.abs(residual) >= 0.5) add('adjust', 'patrimonio', undefined, round2(residual));
  const build = (key: Exclude<StepKey, 'small'>): BridgeStep | null => {
    const m = lines.get(key);
    if (!m) return null;
    const ls = [...m.entries()].map(([k, items]): StepLine => {
      const total = round2(items.reduce((t, x) => t + x.effect, 0));
      return { label: lineLabel(key, k, total), total, items: items.filter(x => x.id).sort((a, b) => Math.abs(b.effect) - Math.abs(a.effect)) };
    }).filter(x => Math.abs(x.total) >= 0.5).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    const total = round2(ls.reduce((t, x) => t + x.total, 0));
    const d = STEP[key];
    return ls.length ? { key, label: d.label[total < 0 ? 0 : 1], short: d.short, note: d.note, total, lines: ls } : null;
  };
  let steps = (['own', 'others', 'reservedPay', 'calendar', 'adjust'] as const).map(build).filter((x): x is BridgeStep => !!x);
  // Los céntimos que no salen en ningún motivo (movimientos de menos de 0,50 €), para que cuadre al céntimo
  const cents = round2(l.now - l.start - savings - steps.reduce((t, s) => t + s.total, 0));
  if (Math.abs(cents) >= 0.005) {
    const adj = steps.find(s => s.key === 'adjust');
    if (adj) { adj.total = round2(adj.total + cents); if (Math.abs(cents) >= 0.5) adj.lines.push({ label: 'Redondeos', total: cents, items: [] }); }
    else steps.push({ key: 'adjust', ...STEP.adjust, label: STEP.adjust.label[0], total: cents, lines: Math.abs(cents) >= 0.5 ? [{ label: 'Redondeos', total: cents, items: [] }] : [] });
  }
  steps = steps.filter(s => Math.abs(s.total) >= 0.005).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  const small = steps.filter(s => Math.abs(s.total) < SMALL_STEP);
  if (small.length >= 2) steps = [...steps.filter(s => !small.includes(s)), {
    key: 'small', label: 'Pequeños ajustes', short: 'Pequeños', note: `Motivos de menos de ${SMALL_STEP} € cada uno: ${small.map(s => s.label.toLowerCase()).join(', ')}.`,
    total: round2(small.reduce((t, s) => t + s.total, 0)), lines: small.flatMap(s => s.lines),
  }];
  return { start: l.start, now: l.now, savings: round2(savings), steps, wealthChange: null };
}
